
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../core-exchange.module.scss';
export const metadata = {
  toc: true,
  subnav: true,
  title: 'Data mapping guide',
  layout: 'guide',
  description: 'Avoid common mapping errors and learn how to handle complex mapping edge cases',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Data mapping guide' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "common-mapping-mistakes-to-avoid"
      }}>{`Common mapping mistakes to avoid`}</h2>
      <h3 {...{
        "id": "sending-only-the-required-fields"
      }}>{`Sending only the required fields`}</h3>
      <p>{`Send every field you can. The more fields you provide, the better data quality your customers will experience when using Plaid-powered apps. Sending just the required fields alone is the most common source of avoidable rework when integrating with Core Exchange.`}</p>
      <h3 {...{
        "id": "reusing-one-transactionid-across-two-transactions"
      }}>{`Reusing one `}<inlineCode parentName="h3">{`transactionId`}</inlineCode>{` across two transactions`}</h3>
      <p>{`A `}<inlineCode parentName="p">{`transactionId`}</inlineCode>{` must be unique within the account, and the `}<inlineCode parentName="p">{`PENDING`}</inlineCode>{` and `}<inlineCode parentName="p">{`POSTED`}</inlineCode>{` versions of the same transaction need different IDs.`}</p>
      <p>{`When two transactions share an ID, the entire transactions response fails validation, so your customer receives no transactions at all. The usual cause is a pair of offsetting entries: a debit and a credit with the same amount, description, and timestamps, posted under a single ID. Give each entry its own ID.`}</p>
      <h3 {...{
        "id": "using-the-wrong-debitcreditmemo"
      }}>{`Using the wrong `}<inlineCode parentName="h3">{`debitCreditMemo`}</inlineCode></h3>
      <p><inlineCode parentName="p">{`amount`}</inlineCode>{` is an absolute value, so `}<inlineCode parentName="p">{`debitCreditMemo`}</inlineCode>{` is the only thing that tells Plaid which direction money moved. `}<inlineCode parentName="p">{`DEBIT`}</inlineCode>{` means money left the account, and `}<inlineCode parentName="p">{`CREDIT`}</inlineCode>{` means money entered it. Don't signal direction by sending a negative `}<inlineCode parentName="p">{`amount`}</inlineCode>{`.`}</p>
      <h3 {...{
        "id": "putting-an-account-in-the-wrong-container"
      }}>{`Putting an account in the wrong container`}</h3>
      <p>{`If your `}<inlineCode parentName="p">{`accountType`}</inlineCode>{` looks right but validation rejects the response, check the container field holding the account, not just the `}<inlineCode parentName="p">{`accountType`}</inlineCode>{` inside it: the `}<inlineCode parentName="p">{`accountCategory`}</inlineCode>{` if using API version 6.0 or later, or the wrapper key such as `}<inlineCode parentName="p">{`depositAccount`}</inlineCode>{` when using version 5.x or earlier. Make sure the container name is spelled correctly (with no typos) and corresponds to a valid enum value for that account type. For example, `}<inlineCode parentName="p">{`LOAN_ACCOUNT`}</inlineCode>{` with a `}<inlineCode parentName="p">{`CREDITCARD`}</inlineCode>{` `}<inlineCode parentName="p">{`accountType`}</inlineCode>{` fails, because `}<inlineCode parentName="p">{`CREDITCARD`}</inlineCode>{` isn't a `}<inlineCode parentName="p">{`LoanAccountType`}</inlineCode>{`.`}</p>
      <h3 {...{
        "id": "omitting-principalbalance-on-a-loan-account-or-sending-the-origination-amount"
      }}>{`Omitting `}<inlineCode parentName="h3">{`principalBalance`}</inlineCode>{` on a loan account, or sending the origination amount`}</h3>
      <p>{`Loan accounts have no `}<inlineCode parentName="p">{`currentBalance`}</inlineCode>{` field. Instead, `}<inlineCode parentName="p">{`principalBalance`}</inlineCode>{` is required on every loan account. Two common mistakes:`}</p>
      <ul>
        <li parentName="ul">{`Omitting `}<inlineCode parentName="li">{`principalBalance`}</inlineCode>{`. Customers see a $0 or empty balance on a loan they know carries a balance.`}</li>
        <li parentName="ul">{`Setting `}<inlineCode parentName="li">{`principalBalance`}</inlineCode>{` as the origination amount instead of the current outstanding balance. The origination amount should go in the `}<inlineCode parentName="li">{`originalPrincipal`}</inlineCode>{` field.`}</li>
      </ul>
      <h3 {...{
        "id": "reporting-available-credit-as-a-line-of-credits-balance"
      }}>{`Reporting available credit as a line of credit's balance`}</h3>
      <p>{`On a line of credit, the three balance figures are distinct, and HELOCs in particular are often mapped incorrectly:`}</p>
      <ul>
        <li parentName="ul"><inlineCode parentName="li">{`currentBalance`}</inlineCode>{` is the amount the customer currently owes.`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`availableCredit`}</inlineCode>{` is what's left to borrow. It's required on every account type except `}<inlineCode parentName="li">{`CHARGE`}</inlineCode>{`, and omitting it on the others fails validation for the whole accounts response.`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`creditLine`}</inlineCode>{` is the credit limit.`}</li>
      </ul>
      <h3 {...{
        "id": "timestamps-that-omit-the-utc-offset-or-that-deviate-from-rfc-3339"
      }}>{`Timestamps that omit the UTC offset, or that deviate from RFC 3339`}</h3>
      <p>{`Timestamp fields take an ISO 8601 date-time in the form `}<inlineCode parentName="p">{`YYYY-MM-DDThh:mm:ss.nnn[Z|[+|-]hh:mm]`}</inlineCode>{`, and date fields take `}<inlineCode parentName="p">{`YYYY-MM-DD`}</inlineCode>{`. Both follow `}<a parentName="p" {...{
          "href": "https://datatracker.ietf.org/doc/html/rfc3339#section-5.6"
        }}>{`IETF RFC 3339`}</a>{`.`}</p>
      <p>{`Deviations that Plaid encounters regularly include placeholder offset values, offsets written without the colon (`}<inlineCode parentName="p">{`-0400`}</inlineCode>{`), ISO 8601 basic-form dates (`}<inlineCode parentName="p">{`20260420`}</inlineCode>{`), a date-only value in a field that expects a full timestamp, and a number instead of a date-time string. Sending incorrect values may cause Plaid to reject the entire transaction, or to associate the transaction with the wrong date.`}</p>
      <h3 {...{
        "id": "an-accountnumberdisplay-that-isnt-the-last-four-digits"
      }}>{`An `}<inlineCode parentName="h3">{`accountNumberDisplay`}</inlineCode>{` that isn't the last four digits`}</h3>
      <p>{`Plaid expects the last four digits of this masked number to match the last four digits of the account number. Values that break this include a currency code, a differently-truncated number such as the first eight digits of a twelve-digit account number, and a number carrying separators.`}</p>
      <p>{`Your customers rely on the mask to recognize their accounts and to detect accounts they've already linked. A mask that changes can also cause the account churn described under `}<a parentName="p" {...{
          "href": "#whats-the-difference-between-accountid-accountnumber-accountnumberdisplay-and-identifier"
        }}><inlineCode parentName="a">{`accountId`}</inlineCode></a>{` below.`}</p>
      <h3 {...{
        "id": "returning-an-empty-accountsaccountidcontact-response"
      }}>{`Returning an empty `}<inlineCode parentName="h3">{`/accounts/{accountId}/contact`}</inlineCode>{` response`}</h3>
      <p><inlineCode parentName="p">{`holders`}</inlineCode>{`, `}<inlineCode parentName="p">{`emails`}</inlineCode>{`, `}<inlineCode parentName="p">{`addresses`}</inlineCode>{`, and `}<inlineCode parentName="p">{`telephones`}</inlineCode>{` are all required, and each needs at least one entry. An empty response fails validation, which fails Identity for the whole account. If your customer records regularly have no email or telephone on file, tell Plaid before you launch.`}</p>
      <h3 {...{
        "id": "omitting-joint-account-holders"
      }}>{`Omitting joint account holders`}</h3>
      <p>{`List every owner of the account in `}<inlineCode parentName="p">{`holders`}</inlineCode>{`, each with the `}<inlineCode parentName="p">{`relationship`}</inlineCode>{` that describes them. Two common errors are returning only one holder for a jointly held account, and marking a sole owner `}<inlineCode parentName="p">{`SECONDARY`}</inlineCode>{`.`}</p>
      <p>{`Note that Plaid doesn't associate `}<inlineCode parentName="p">{`emails`}</inlineCode>{`, `}<inlineCode parentName="p">{`addresses`}</inlineCode>{`, or `}<inlineCode parentName="p">{`telephones`}</inlineCode>{` with individual holders, so contact details for every owner belong in those account-level arrays.`}</p>
      <h3 {...{
        "id": "returning-fully-divested-positions-as-holdings"
      }}>{`Returning fully divested positions as holdings`}</h3>
      <p>{`A holding represents a position the customer currently holds. When a sold-out position stays in the `}<inlineCode parentName="p">{`holdings`}</inlineCode>{` array with `}<inlineCode parentName="p">{`units`}</inlineCode>{` and `}<inlineCode parentName="p">{`marketValue`}</inlineCode>{` set to `}<inlineCode parentName="p">{`0`}</inlineCode>{`, Plaid passes it through as a live holding, and because most systems keep repricing it, the customer sees a position they no longer own with a price that updates daily. Omit positions the customer has fully divested.`}</p>
      <h3 {...{
        "id": "reporting-borrowing-power-as-available-cash"
      }}>{`Reporting borrowing power as available cash`}</h3>
      <p><inlineCode parentName="p">{`availableCashBalance`}</inlineCode>{` is the cash held across all sub-accounts, including sweep funds. On a margin-enabled account, don't map buying power or margin availability into it: doing so reports borrowed funds as cash the customer holds. Likewise, `}<inlineCode parentName="p">{`currentValue`}</inlineCode>{` is the total current value of all investments, not the account's purchasing power.`}</p>
      <p>{`A cash or sweep position also needs `}<inlineCode parentName="p">{`cashAccount`}</inlineCode>{` set to `}<inlineCode parentName="p">{`true`}</inlineCode>{`, or an explicit `}<inlineCode parentName="p">{`isCashEquivalent`}</inlineCode>{` value in `}<inlineCode parentName="p">{`fiAttributes`}</inlineCode>{`, so that Plaid can classify it as cash rather than as a security.`}</p>
      <h2 {...{
        "id": "data-mapping-faqs"
      }}>{`Data mapping FAQs`}</h2>
      <h3 {...{
        "id": "what-if-we-cant-source-a-required-field-such-as-availablebalance"
      }}>{`What if we can't source a required field, such as `}<inlineCode parentName="h3">{`availableBalance`}</inlineCode>{`?`}</h3>
      <p>{`If you can't provide a required field, contact Plaid to discuss possible solutions. Don't omit the field and don't substitute a value you do have.`}</p>
      <h3 {...{
        "id": "are-credit-cards-a-line-of-credit-or-a-loan"
      }}>{`Are credit cards a line of credit or a loan?`}</h3>
      <p>{`In Core Exchange, if the customer can pay the balance down and draw on it again, use line of credit; if they can only pay it off, use loan. Credit cards therefore correspond to a line of credit with `}<inlineCode parentName="p">{`accountType`}</inlineCode>{` `}<inlineCode parentName="p">{`CREDITCARD`}</inlineCode>{`, under `}<inlineCode parentName="p">{`accountCategory`}</inlineCode>{` `}<inlineCode parentName="p">{`LOC_ACCOUNT`}</inlineCode>{` (or the `}<inlineCode parentName="p">{`locAccount`}</inlineCode>{` wrapper key if using an API version earlier than 6.0).`}</p>
      <h3 {...{
        "id": "whats-the-difference-between-accountid-accountnumber-accountnumberdisplay-and-identifier"
      }}>{`What's the difference between `}<inlineCode parentName="h3">{`accountId`}</inlineCode>{`, `}<inlineCode parentName="h3">{`accountNumber`}</inlineCode>{`, `}<inlineCode parentName="h3">{`accountNumberDisplay`}</inlineCode>{`, and `}<inlineCode parentName="h3">{`identifier`}</inlineCode>{`?`}</h3>
      <p><inlineCode parentName="p">{`accountId`}</inlineCode>{` is the account's long-term identity within your organization. It isn't an account number, shouldn't be derived from one, and should never change, even if the account number changes. If it changes, Plaid sees a new account rather than the same one, which causes "account churn" and disruption for your customers using Plaid-linked accounts for long-lived use cases, such as personal financial management.`}</p>
      <p><inlineCode parentName="p">{`accountNumberDisplay`}</inlineCode>{` is the masked number your customer recognizes.`}</p>
      <p><inlineCode parentName="p">{`accountNumber`}</inlineCode>{` is the full account number, and is only used for loan accounts.`}</p>
      <p>{`For depository accounts, the account number should be returned in the `}<inlineCode parentName="p">{`identifier`}</inlineCode>{` field of `}<inlineCode parentName="p">{`GET /accounts/{accountId}/payment-networks`}</inlineCode>{`. If this number ever changes, begin returning the new number, and Plaid will detect the new number if it ever changes.`}</p>
      <h3 {...{
        "id": "how-much-transaction-history-should-we-serve"
      }}>{`How much transaction history should we serve?`}</h3>
      <p>{`Serve as much as your systems retain, up to 24 months. Use cases such as mortgage underwriting and cash flow analysis depend on the full two years being available. Sending more than 24 months does not cause an error, but is unnecessary: Plaid will simply discard any transaction data beyond 730 days.`}</p>
      <p>{`Note: when Plaid requests a start date older than you support, return the FDX `}<inlineCode parentName="p">{`invalid date range`}</inlineCode>{` error, code `}<inlineCode parentName="p">{`703`}</inlineCode>{`, with an HTTP 400 status. Returning a shorter window or an empty array instead can look like missing transactions data and result in support tickets.`}</p>
      <h3 {...{
        "id": "how-should-we-model-multi-currency-or-foreign-currency-accounts"
      }}>{`How should we model multi-currency or foreign currency accounts?`}</h3>
      <p>{`Every account has exactly one required `}<inlineCode parentName="p">{`currency.currencyCode`}</inlineCode>{`, so there's no way to express a single account holding several currencies.`}</p>
      <p>{`Supported payment networks compatible with Core Exchange cover US and Canadian rails only. Accounts denominated in currencies other than USD or CAD can be listed with balances and transactions, but they aren't Auth-eligible. If your product is built around multi-currency or non-North-American rails, consult with Plaid before you build.`}</p>
      <h3 {...{
        "id": "our-security-has-no-cusip-isin-sedol-or-ticker-what-do-we-send"
      }}>{`Our security has no CUSIP, ISIN, SEDOL, or ticker. What do we send?`}</h3>
      <p>{`Some securities, such as private funds, non-traded partnerships, and physical collectibles may not have identifier symbols. In this case, set `}<inlineCode parentName="p">{`holdingName`}</inlineCode>{` to the security's name, `}<inlineCode parentName="p">{`holdingType`}</inlineCode>{` to `}<inlineCode parentName="p">{`OTHER`}</inlineCode>{`, and `}<inlineCode parentName="p">{`units`}</inlineCode>{` to the number held, and omit `}<inlineCode parentName="p">{`symbol`}</inlineCode>{` along with `}<inlineCode parentName="p">{`securityIds`}</inlineCode>{` (or `}<inlineCode parentName="p">{`securityId`}</inlineCode>{` and `}<inlineCode parentName="p">{`securityIdType`}</inlineCode>{` if using an API version earlier than 6.0).`}</p>
      {/* prettier-ignore */}
      <pre><code parentName="pre" {...{
          "className": "language-json",
          "metastring": "#Example",
          "#Example": true
        }}>{`{
  "holdingName": "1794 Flowing Hair Dollar",
  "holdingType": "OTHER",
  "units": 1,
  "marketValue": 7500000.00,
  "cashAccount": false,
  "currency": {
    "currencyCode": "USD"
  }
}
`}</code></pre>
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "common-mapping-mistakes-to-avoid",
        level: 2,
        title: "Common mapping mistakes to avoid",
      },
{
        id: "sending-only-the-required-fields",
        level: 3,
        title: "Sending only the required fields",
      },
{
        id: "reusing-one-transactionid-across-two-transactions",
        level: 3,
        title: "Reusing one transactionId across two transactions",
      },
{
        id: "using-the-wrong-debitcreditmemo",
        level: 3,
        title: "Using the wrong debitCreditMemo",
      },
{
        id: "putting-an-account-in-the-wrong-container",
        level: 3,
        title: "Putting an account in the wrong container",
      },
{
        id: "omitting-principalbalance-on-a-loan-account-or-sending-the-origination-amount",
        level: 3,
        title: "Omitting principalBalance on a loan account, or sending the origination amount",
      },
{
        id: "reporting-available-credit-as-a-line-of-credits-balance",
        level: 3,
        title: "Reporting available credit as a line of credit's balance",
      },
{
        id: "timestamps-that-omit-the-utc-offset-or-that-deviate-from-rfc-3339",
        level: 3,
        title: "Timestamps that omit the UTC offset, or that deviate from RFC 3339",
      },
{
        id: "an-accountnumberdisplay-that-isnt-the-last-four-digits",
        level: 3,
        title: "An accountNumberDisplay that isn't the last four digits",
      },
{
        id: "returning-an-empty-accountsaccountidcontact-response",
        level: 3,
        title: "Returning an empty /accounts/{accountId}/contact response",
      },
{
        id: "omitting-joint-account-holders",
        level: 3,
        title: "Omitting joint account holders",
      },
{
        id: "returning-fully-divested-positions-as-holdings",
        level: 3,
        title: "Returning fully divested positions as holdings",
      },
{
        id: "reporting-borrowing-power-as-available-cash",
        level: 3,
        title: "Reporting borrowing power as available cash",
      },
{
        id: "data-mapping-faqs",
        level: 2,
        title: "Data mapping FAQs",
      },
{
        id: "what-if-we-cant-source-a-required-field-such-as-availablebalance",
        level: 3,
        title: "What if we can't source a required field, such as availableBalance?",
      },
{
        id: "are-credit-cards-a-line-of-credit-or-a-loan",
        level: 3,
        title: "Are credit cards a line of credit or a loan?",
      },
{
        id: "whats-the-difference-between-accountid-accountnumber-accountnumberdisplay-and-identifier",
        level: 3,
        title: "What's the difference between accountId, accountNumber, accountNumberDisplay, and identifier?",
      },
{
        id: "how-much-transaction-history-should-we-serve",
        level: 3,
        title: "How much transaction history should we serve?",
      },
{
        id: "how-should-we-model-multi-currency-or-foreign-currency-accounts",
        level: 3,
        title: "How should we model multi-currency or foreign currency accounts?",
      },
{
        id: "our-security-has-no-cusip-isin-sedol-or-ticker-what-do-we-send",
        level: 3,
        title: "Our security has no CUSIP, ISIN, SEDOL, or ticker. What do we send?",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
